import clsx from 'clsx'
import { useRouter } from 'next/router'
import React, { useMemo } from 'react'
import Link from 'next/link'

import type { IconProps } from '@wartek-id/icon'
import { Icon } from '@wartek-id/icon'
import type { ButtonProps } from '@wartek-id/button-v1'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text'
import { Shimmer } from '@wartek-id/shimmer'
import { Badge } from '@wartek-id/badge'

import {
  allSelectionDocumentTypes,
  useQueryAPIGetKSInvitationDocuments,
  useQueryAPIGetKSSelectionCandidate,
  useQueryApiGetKSSelectionResult,
  useQueryAPIGetVerificationDocuments,
} from 'api/bcks/useAPIBCKSInvitation'
import VerticalProgressItem from './VerticalProgressItem'
import { getDayToDeadlineState } from '../utils/date'
import useKSPSTrackEvent from '../hooks/useKSPSTrackEvent'
import { useAPIPhicosGetTokenUploadPhoto } from 'api/bcks/useAPIExternal'

import type { SelectionCandidateState } from 'api/bcks/useAPIBCKSInvitation'

const SELECTION_STEP_1_DOC_REQUIREMENTS = [
  {
    description:
      'Hasil Penilaian Kinerja (diterbitkan dalam 2 tahun terakhir dan dapat dihitung dari sebelum menjabat sebagai guru PPPK)',
    subRequirements: [],
  },
  {
    description:
      'SK Pengalaman Manajerial (diterbitkan dalam 2 tahun terakhir)',
    subRequirements: ['Khusus PPPK lampirkan juga bukti pengalaman kerja'],
  },
  {
    description:
      'SK Bebas Hukuman Disiplin (diterbitkan dalam 2 tahun terakhir)',
    subRequirements: [],
  },
  {
    description: 'SKCK (diterbitkan dalam 6 bulan terakhir)',
    subRequirements: [],
  },
  {
    description: 'Pakta Integritas',
    subRequirements: [],
  },
]
const NUM_OF_REQUIRED_DOCS = 5
const LMS_URL = process.env.NEXT_PUBLIC_LMS_DIKLAT_URL + '?source=bcks'

const LinkBCKSGuide = (buttonProps: ButtonProps) => {
  const { KSPSTrackEvent } = useKSPSTrackEvent()

  return (
    <Link href="/bcks/invitation/guide" passHref>
      <Button
        fullWidth
        color="black"
        onClick={() => {
          KSPSTrackEvent({
            event_name: 'click_ks_selection_invitation_file_description',
          })
        }}
        {...buttonProps}
      >
        Baca penjelasan tiap berkas
      </Button>
    </Link>
  )
}

export const SelectionSteps = ({ staticView = false }) => {
  const router = useRouter()
  const { KSPSTrackEvent } = useKSPSTrackEvent()

  const { data: candidate, isLoading: isLoadingSelectionState } =
    useQueryAPIGetKSSelectionCandidate({
      enabled: !staticView,
    })

  const candidateState = candidate?.selectionCandidateState
  const selectionState = candidate?.selectionState
  const selectionType = candidate?.selectionType
  const reason = candidate?.reason

  const { data: documentState, isLoading: isLoadingDocuments } =
    useQueryAPIGetKSInvitationDocuments(candidate?.selectionId || '')

  const { data: result } = useQueryApiGetKSSelectionResult()

  const {
    data: verificationDocuments,
    isLoading: isLoadingVerificationDocuments,
  } = useQueryAPIGetVerificationDocuments(candidate?.selectionId, {
    enabled: candidateState === 'documentVerification',
  })
  const deadlineState = getDayToDeadlineState(candidate?.deadlineDate)

  const {
    refetch: getTokenUploadPhoto,
    isLoading: isLoadingToken,
    isRefetching: isRefetchingToken,
  } = useAPIPhicosGetTokenUploadPhoto()
  const isGettingToken = isLoadingToken || isRefetchingToken

  const isLoading =
    isLoadingSelectionState ||
    isLoadingDocuments ||
    isLoadingVerificationDocuments

  const isOnStepUploadDocs =
    [
      'documentSubmission',
      'documentVerification',
      'documentVerificationFailed',
      'adminFinished',
    ].includes(candidateState) &&
    candidate?.selectionState === 'documentVerification' &&
    selectionType === 'administration'

  const isNapzaStep = [
    'napzaInvitation',
    'napzaVerification',
    'napzaFinished',
    'napzaFailed',
  ].includes(candidateState)

  const isSubstanceStep =
    candidateState === 'substanceInProgress' ||
    (candidateState === 'adminFinished' && selectionState === 'finished')
  const isCandidateFailed = ['adminFailed', 'adminCanceled'].includes(
    selectionState
  )

  const isOnDiklatStep = [
    'substanceFinished',
    'diklatPending',
    'diklatInProgress',
  ].includes(candidateState)

  const showActionButton =
    !isLoading &&
    candidateState === 'documentSubmission' &&
    deadlineState === 'withinDeadline'

  const numOfUploadedDoc = useMemo(() => {
    let count = 0
    if (documentState) {
      Object.keys(documentState).forEach((key) => {
        if (key.includes('ThumbnailUrl') && documentState[key].length > 0) {
          count++
        }
      })
    }

    return count
  }, [documentState])

  const hasRevisions = useMemo(
    () =>
      allSelectionDocumentTypes.some(
        (docType) => verificationDocuments?.[docType]?.state === 'NEED_REVISION'
      ),
    [verificationDocuments]
  )

  const handleActionButtonClick = () => {
    KSPSTrackEvent({
      event_name: 'click_ks_selection_process_upload_file',
    })

    if (numOfUploadedDoc === NUM_OF_REQUIRED_DOCS) {
      router.push('/bcks/invitation/review')
    } else {
      router.push('/bcks/invitation/collection')
    }
  }

  const handleGetRedirection = async () => {
    try {
      const { data } = await getTokenUploadPhoto()
      window.open(
        `${process.env.NEXT_PUBLIC_PHICOS_BASE_URL}/upload-photo/${data.token}`
      )
    } catch {}
  }

  const SELECTION_STEPS = [
    {
      title: 'Pembukaan Seleksi',
      description:
        'Undangan akan dikirimkan via Ruang GTK untuk Kandidat BCKS (Bakal Calon Kepala Sekolah) yang sudah memenuhi syarat.',
      isDone:
        !staticView && !['notInvited', 'invitation'].includes(candidateState),
    },
    {
      title: 'Seleksi Administrasi',
      isActive: isOnStepUploadDocs || isNapzaStep,
      description: (
        <>
          {hasRevisions && (
            <Badge variant="warning" className="mt-2">
              Perlu diperbaiki
            </Badge>
          )}

          <Text variant="body-sm" color="subdued" className="mt-1.5">
            Tahap pertama adalah Seleksi Administrasi. Kumpulkan berkas berikut
            yang asli (<strong>bukan fotokopi</strong>), format{' '}
            <strong>PDF</strong>, ukuran <strong>maks. 5 MB</strong>:
          </Text>

          <ol className="list-decimal list-outside text-[13px] text-subdued pl-5 mb-5">
            {SELECTION_STEP_1_DOC_REQUIREMENTS.map(
              ({ description, subRequirements }, index) => (
                <li key={`text-${index}`}>
                  <Text variant="body-sm" color="subdued">
                    {description}
                  </Text>
                  <ol className="pl-5 list-outside list-[lower-alpha]">
                    {subRequirements.map((req, reqIndex) => (
                      <li key={`sub-${reqIndex}`}>
                        <Text variant="body-sm" color="subdued">
                          {req}
                        </Text>
                      </li>
                    ))}
                  </ol>
                </li>
              )
            )}
          </ol>

          {isOnStepUploadDocs && (
            <Text variant="body-sm" color="subdued" className="mt-3">
              Dinas setempat akan melakukan verifikasi dan validasi berkas.
            </Text>
          )}

          <div className="mt-1.5">
            {isLoading ? (
              <Shimmer height={24} className="!w-full" />
            ) : (
              <>
                {[
                  'documentVerification',
                  'documentVerificationFailed',
                  'adminFinished',
                ].includes(candidateState) &&
                  candidate?.selectionState === 'documentVerification' &&
                  selectionType === 'administration' &&
                  !hasRevisions && (
                    <Badge variant="informational">
                      Berkas sedang dicek oleh Dinas
                    </Badge>
                  )}

                {hasRevisions && (
                  <Link href="/bcks/invitation/revision" passHref>
                    <Button fullWidth color="white">
                      Perbaiki berkas
                    </Button>
                  </Link>
                )}
              </>
            )}
          </div>

          {showActionButton && (
            <div className="mt-1.5">
              <Button fullWidth color="blue" onClick={handleActionButtonClick}>
                {isLoading
                  ? ''
                  : numOfUploadedDoc > 0
                  ? 'Lanjutkan draf'
                  : 'Unggah berkas'}
              </Button>
            </div>
          )}

          {(staticView ||
            ['notInvited', 'invitation', 'documentSubmission'].includes(
              candidateState
            )) && (
            <div className="mt-1.5">
              <LinkBCKSGuide
                color={
                  candidateState === 'documentSubmission' ? 'white' : 'black'
                }
              />
            </div>
          )}
        </>
      ),
      isDone: isSubstanceStep || selectionType === 'substance',
      isFailed: isCandidateFailed,
    },
    {
      title: 'Seleksi Substansi',
      description: 'Seleksi dilaksanakan secara luring.',
      isActive: isSubstanceStep,
      isDone: isOnDiklatStep,
      isFailed: candidateState === 'substanceFailed',
    },
    {
      title: 'Pelatihan',
      description: (
        <div className="w-full">
          <Text variant="body-sm" color="subdued" className="my-1.5">
            Seleksi dilaksanakan secara luring.
          </Text>
          {isOnDiklatStep && (
            <a href={LMS_URL} target="_blank" rel="noopener noreferrer">
              <Button fullWidth>Cek kelas pelatihan</Button>
            </a>
          )}
        </div>
      ),
      isActive: isOnDiklatStep,
    },
    {
      title: 'Pengumpulan Dokumen Akhir',
      description: 'Pengumpulan dokumen yang diperlukan.',
    },
    {
      title: 'Informasi Hasil Seleksi',
      description: 'Dinas Pendidikan setempat akan menghubungi Anda.',
    },
  ]

  const getSubstanceState = (selectionState: SelectionCandidateState) => {
    switch (selectionState) {
      case 'substanceInProgress':
        return {
          icon: 'check_circle',
          iconColor: 'success',
          bgColor: 'bg-surface-success-subdued',
          borderColor: 'border-surface-success-default',
          title: 'Anda lolos Seleksi Administrasi',
          desc: 'Anda mendapat undangan untuk mengikuti tahap berikutnya. Hubungi Dinas untuk selengkapnya.',
        }
      case 'substanceFinished':
      case 'diklatInProgress':
      case 'diklatPending':
        return {
          icon: 'check_circle',
          iconColor: 'success',
          bgColor: 'bg-surface-success-subdued',
          borderColor: 'border-surface-success-default',
          title: 'Anda lulus Seleksi Substansi',
          desc: 'Anda diharapkan untuk melaporkan diri ke Dinas Pendidikan setempat. Selanjutnya Anda akan dikelaskan dalam pelatihan BCKS.',
        }
      case 'adminFailed':
      case 'adminCanceled':
      case 'substanceFailed':
        return {
          icon: 'close',
          iconColor: 'critical',
          bgColor: 'bg-white',
          borderColor: 'border-critical-default',
          title: 'Maaf, Anda tidak dilanjutkan ke tahap berikutnya',
          desc: 'Terima kasih atas partisipasi Anda.',
        }
      case 'adminFinished':
        return {
          icon: 'hourglass_empty',
          iconColor: 'default',
          bgColor: 'bg-surface-selected-default',
          borderColor: 'border-default',
          title: 'Mohon tunggu informasi selanjutnya',
          desc: ' Hubungi Dinas untuk selengkapnya.',
        }
    }

    return undefined
  }
  const substanceState = getSubstanceState(candidateState)

  const getNapzaState = (selectionState: SelectionCandidateState) => {
    switch (selectionState) {
      case 'napzaInvitation':
        return {
          desc: 'Dokumen NAPZA Anda diperlukan oleh Dinas untuk menyelesaikan proses seleksi.',
        }
      case 'napzaVerification':
        return {
          desc: 'Dokumen NAPZA Anda diperlukan oleh Dinas untuk menyelesaikan proses seleksi.',
          badgeText: 'Berkas sedang dicek oleh Dinas',
          badgeColor: '!bg-[#98DAFB]',
        }
      case 'napzaFinished':
        return {
          desc: 'Dokumen NAPZA Anda sudah diverifikasi oleh Dinas. Mohon tunggu informasi selanjutnya.',
          badgeText: 'Sudah diverifikasi',
          badgeColor: '!bg-[#E2F5E0]',
        }
      case 'napzaFailed':
        return {
          desc: 'Dokumen NAPZA Anda sudah diverifikasi oleh Dinas.',
          badgeText: 'Verifikasi gagal',
          badgeColor: '!bg-[#FFEDCE]',
        }
    }
  }
  const napzaState = getNapzaState(candidateState)

  return (
    <div>
      {selectionState === 'finished' && substanceState && (
        <div className="p-4 bg-surface-default">
          <div
            className={clsx(
              'px-2.5 py-5 rounded flex flex-col items-center gap-2 border',
              substanceState.bgColor,
              substanceState.borderColor
            )}
          >
            <Icon
              className="!text-[40px] !w-10 !h-10"
              color={substanceState.iconColor as IconProps['color']}
            >
              {substanceState.icon}
            </Icon>
            <Text variant="heading-sm" className="text-center">
              {substanceState.title}
            </Text>

            {reason ? (
              <Text variant="body-sm" color="subdued">
                Alasan:
                <br />
                <ul className="my-1 list-disc pl-4">
                  {(() => {
                    try {
                      return JSON.parse(reason).map(
                        (item: string, index: number) => (
                          <li key={index}>{item}</li>
                        )
                      )
                    } catch {
                      return <li>Gagal memuat alasan.</li>
                    }
                  })()}
                </ul>
                Terima kasih atas partisipasi Anda.
              </Text>
            ) : (
              <Text variant="body-sm" className="text-center" color="subdued">
                {substanceState.desc}
              </Text>
            )}
          </div>

          {candidateState === 'substanceInProgress' && (
            <div className="mt-6">
              <Button
                fullWidth
                className="mb-1.5"
                isLoading={isGettingToken}
                disabled={isGettingToken}
                onClick={handleGetRedirection}
              >
                Unggah foto peserta
              </Button>
              {result?.skFileUrl && (
                <a href={result.skFileUrl} target="_blank" rel="noreferrer">
                  <Button fullWidth color="white">
                    Unduh Berita Acara
                  </Button>
                </a>
              )}
            </div>
          )}

          {isOnDiklatStep && (
            <div className="mt-3">
              <a href={LMS_URL} target="_blank" rel="noreferrer">
                <Button fullWidth>Cek diklat pelatihan</Button>
              </a>
            </div>
          )}
        </div>
      )}

      <div className="p-3">
        {napzaState && (
          <div className="bg-surface-default p-3 rounded flex flex-col gap-3 border border-subdued mb-6">
            <Text variant="heading-sm">Unggah Dokumen NAPZA</Text>
            <Text variant="body-sm" color="subdued">
              {napzaState.desc}
            </Text>

            {napzaState.badgeText ? (
              <Badge className={`w-fit ${napzaState.badgeColor}`}>
                {napzaState.badgeText}
              </Badge>
            ) : (
              <Link href="/bcks/invitation/napza" passHref>
                <Button className="w-fit" color="black">
                  Unggah Dokumen
                </Button>
              </Link>
            )}
          </div>
        )}

        {SELECTION_STEPS.map((step, i) => (
          <VerticalProgressItem
            key={`step-${i}`}
            step={i + 1}
            className="pb-4"
            withVerticalLine={i < SELECTION_STEPS.length - 1}
            isDone={step.isDone}
            isActive={step.isActive}
            isFailed={step.isFailed}
          >
            <Text variant="heading-sm">{step.title}</Text>
            {typeof step.description === 'string' ? (
              <Text variant="body-sm" color="subdued" className="mt-1.5">
                {step.description}
              </Text>
            ) : (
              step.description
            )}
          </VerticalProgressItem>
        ))}
      </div>
    </div>
  )
}
